<template>
  <div class="iw">
    <header class="iw-top">
      <router-link class="iw-brand" :to="homeTarget" title="Inventory home">
        <span class="iw-logo" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.9" stroke-linecap="round" stroke-linejoin="round">
            <path class="iw-logo-l1" d="M12 3l8 4-8 4-8-4z" />
            <path class="iw-logo-l2" d="M4 12l8 4 8-4" />
            <path class="iw-logo-l3" d="M4 16.5l8 4 8-4" />
          </svg>
        </span>
        <span class="iw-brand-text">
          <span class="iw-brand-name">Inventory</span>
          <span class="iw-brand-sub">{{ brandName }}</span>
        </span>
      </router-link>

      <button type="button" class="iw-back" title="Back to Connect" @click="backToConnect">
        <InvIcon name="arrow-left" /><span>Connect</span>
      </button>

      <span class="iw-rule" aria-hidden="true"></span>

      <nav class="iw-areas" aria-label="Inventory sections" data-tour="areas" ref="areas">
        <router-link
          v-for="a in areas"
          :key="a.title"
          :to="areaLink(a)"
          class="iw-area"
          :class="{ 'is-on': here.area && here.area.title === a.title }"
          :data-area="a.title"
          :title="$tr(a.title)"
        >
          <InvIcon :name="a.icon" class="iw-area-icon" />
          <span class="iw-area-text">{{ $tr(a.title) }}</span>
        </router-link>
        <span class="iw-ink" :style="areaInk" aria-hidden="true"></span>
      </nav>

      <div class="iw-tools">
        <button type="button" class="iw-tool iw-menu-btn" title="All Inventory pages" @click="openFinder">
          <InvIcon name="list" />
        </button>

        <label class="iw-store" v-if="hasStores" data-tour="store" :title="scopeTitle">
          <InvIcon name="store" class="iw-store-icon" />
          <select class="iw-select" :value="selectedStore" @change="switchStore($event.target.value)" aria-label="Store">
            <template v-if="canPickAll">
              <option :value="ALL">{{ $tr(allLabel) }}</option>
              <optgroup label="One store">
                <option v-for="s in stores" :key="s.shash" :value="s.shash">{{ s.s_name }}</option>
              </optgroup>
            </template>
            <template v-else>
              <option v-for="s in stores" :key="s.shash" :value="s.shash">{{ s.s_name }}</option>
            </template>
          </select>
        </label>

        <button type="button" class="iw-find" data-tour="find" title="Find a page (Ctrl K)" @click="openFinder">
          <InvIcon name="search" /><span class="iw-find-text">Find</span><kbd>{{ isMac ? "⌘" : "Ctrl" }} K</kbd>
        </button>

        <button type="button" class="iw-tool" :class="{ 'is-on': drawerOpen && drawerTab === 'approvals' }" title="Approvals" @click="$store.dispatch('toggleDrawer', 'approvals')">
          <InvIcon name="approvals" />
          <span class="iw-badge" v-if="approvalsCount">{{ approvalsCount > 99 ? "99+" : approvalsCount }}</span>
        </button>
        <button type="button" class="iw-tool" :class="{ 'is-on': drawerOpen && drawerTab === 'chat' }" title="Team chat" @click="$store.dispatch('toggleDrawer', 'chat')">
          <InvIcon name="chat" />
          <span class="iw-badge" v-if="chatUnread">{{ chatUnread > 99 ? "99+" : chatUnread }}</span>
        </button>
        <button
          v-if="assistantAllowed"
          type="button"
          class="iw-tool iw-ask"
          data-tour="ask"
          :class="{ 'is-on': drawerOpen && drawerTab === 'assistant' }"
          title="Ask Connect: questions, invoices, price lists"
          @click="$store.dispatch('toggleDrawer', 'assistant')"
        >
          <InvIcon name="sparkle" />
        </button>

        <div class="iw-pop-wrap" ref="helpWrap">
          <button type="button" class="iw-tool" data-tour="help" title="Help" :class="{ 'is-on': helpMenu }" @click="helpMenu = !helpMenu">
            <InvIcon name="help" />
          </button>
          <transition name="iw-pop">
            <div v-if="helpMenu" class="iw-pop" role="menu">
              <button type="button" class="iw-pop-item" role="menuitem" :disabled="!help.available" @click="openPageHelp">
                <InvIcon name="info" /><span><strong>How this page works</strong><small>{{ help.available ? "The steps, in plain words" : "This page has no guide yet" }}</small></span>
              </button>
              <button type="button" class="iw-pop-item" role="menuitem" @click="startTour">
                <InvIcon name="compass" /><span><strong>Take the tour</strong><small>A one-minute walk around Inventory</small></span>
              </button>
              <button type="button" class="iw-pop-item" role="menuitem" @click="openFinder">
                <InvIcon name="search" /><span><strong>Find a page</strong><small>Every Inventory page, by name</small></span>
              </button>
            </div>
          </transition>
        </div>

        <button type="button" class="iw-tool" :title="theme === 'light' ? $tr('Switch to dark') : $tr('Switch to light')" @click="$store.commit('TOGGLE_THEME')">
          <InvIcon :name="theme === 'light' ? 'moon' : 'sun'" />
        </button>

        <div class="iw-pop-wrap" ref="userWrap">
          <button type="button" class="iw-user" :title="userName" @click="userMenu = !userMenu">
            <span class="iw-avatar">{{ initials }}</span>
          </button>
          <transition name="iw-pop">
            <div v-if="userMenu" class="iw-pop iw-pop-user" role="menu">
              <div class="iw-pop-head">
                <strong>{{ userName }}</strong>
                <small>{{ me.roleName || "—" }}</small>
              </div>
              <button type="button" class="iw-pop-item" role="menuitem" @click="backToConnect"><InvIcon name="arrow-left" /><span><strong>Back to Connect</strong></span></button>
              <button type="button" class="iw-pop-item" role="menuitem" @click="logout"><InvIcon name="x" /><span><strong>Sign out</strong></span></button>
            </div>
          </transition>
        </div>
      </div>
    </header>

    <div class="iw-sub" v-if="here.area">
      <nav class="iw-pages" aria-label="Pages" data-tour="pages" ref="pages">
        <router-link
          v-for="p in here.area.pages"
          :key="p.name + (p.query ? JSON.stringify(p.query) : '')"
          :to="pageTarget(p)"
          class="iw-page"
          :class="{ 'is-on': here.page === p }"
          :data-page="p.name"
        >
          <InvIcon :name="p.icon" class="iw-page-icon" />{{ $tr(p.title) }}
        </router-link>
        <span class="iw-ink is-sub" :style="pageInk" aria-hidden="true"></span>
      </nav>
    </div>

    <main class="iw-main" :class="{ 'no-sub': !here.area }" ref="main">
      <div class="iw-canvas">
        <slot />
      </div>
    </main>

    <InvFinder v-if="finder" :areas="areas" @close="finder = false" />
    <InvTour v-if="tour" :steps="tourSteps" @done="endTour" />
  </div>
</template>

<script>
import { reactive } from "vue";
import { mapState } from "vuex";
import brand from "@/brand";
import { ALL_STORES_LABEL } from "@/store/modules/common/stores";
import InvIcon from "@/views/Inventory/kit/InvIcon.vue";
import InvFinder from "@/views/Inventory/kit/InvFinder.vue";
import InvTour from "@/views/Inventory/kit/InvTour.vue";
import { inventoryAreas, locate, areaTarget, pageTarget } from "@/views/Inventory/kit/invNav.js";
import { tr, trf } from "@/i18n";

// The <select> value of "All stores"; never a store hash (as in TopBar.vue).
const ALL = "*all*";
const LAST_CONNECT_PAGE = "connect:last-page";
// The page last opened in each section, for this tab: a section's tab takes you back to where you were in it.
const LAST_IN_AREA = "inv:last-in-area";
const readLastInArea = () => {
  try { return JSON.parse(sessionStorage.getItem(LAST_IN_AREA) || "{}") || {}; } catch (e) { return {}; }
};

/**
 * Inventory as a workspace of its own inside Connect (owner 2026-11-02): opened from the top bar, in the same tab,
 * with its own header (Kubi's sections as tabs, the section's pages under them) instead of Connect's sidebar.
 * Everything that is Connect's stays: the store picker (one rule with TopBar.vue), Approvals, the team chat and the
 * assistant in the right drawer, the theme, the account. Dashboard.vue renders it for the workspace's routes.
 *
 * Pages reach it through inject("invWorkspace"): InvShell registers its "How it works" guide there, so the help
 * menu can open it.
 */
export default {
  name: "InventoryWorkspace",
  components: { InvIcon, InvFinder, InvTour },
  provide() {
    return { invWorkspace: this.help };
  },
  data() {
    return {
      ALL,
      allLabel: ALL_STORES_LABEL,
      finder: false,
      tour: false,
      helpMenu: false,
      userMenu: false,
      areaInk: { opacity: 0 },
      pageInk: { opacity: 0 },
      // What the page on screen offers: its guide (InvShell sets available, and opens it when open turns true).
      help: reactive({ available: false, open: false }),
      lastInArea: readLastInArea(),
      isMac: typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform || ""),
    };
  },
  computed: {
    ...mapState({
      theme: (s) => s.common.theme,
      drawerOpen: (s) => s.panel.drawerOpen,
      drawerTab: (s) => s.panel.drawerTab,
      me: (s) => s.rbac.me,
      assistantAllowed: (s) => s.assistant.allowed,
      stateStores: "stores",
    }),
    brandName() {
      return brand.name || "Connect";
    },
    areas() {
      // Reads the role's permissions and "All stores", so it follows both.
      return inventoryAreas(this.$store);
    },
    here() {
      return locate(this.areas, this.$route);
    },
    homeTarget() {
      return this.areas.length ? areaTarget(this.areas[0]) : { name: "inventory-dashboard" };
    },
    stores() {
      return Array.isArray(this.stateStores.stores) ? this.stateStores.stores : [];
    },
    hasStores() {
      return this.stores.length > 0;
    },
    canPickAll() {
      return this.$store.getters.mayPickAllStores && this.$store.getters.brandStores.length > 1;
    },
    selectedStore() {
      return this.stateStores.current.allStores ? ALL : this.stateStores.current.storeHash;
    },
    scopeTitle() {
      if (this.stateStores.current.allStores) return tr("Every store of the brand, added together");
      return this.me.loaded && !this.me.allStores ? trf("Your account covers {0} of the stores", [this.me.stores.length]) : tr("Which store Inventory is looking at");
    },
    approvalsCount() {
      return this.$store.getters.approvalsCount;
    },
    chatUnread() {
      return this.$store.getters.chatUnread;
    },
    userName() {
      return (this.$store.getters.getUser && this.$store.getters.getUser.name) || "—";
    },
    initials() {
      return String(this.userName).split(/\s+/).map((w) => w.charAt(0)).join("").slice(0, 2).toUpperCase() || "?";
    },
    tourKey() {
      return "inv:tour:v1:" + (this.me.uid || "me");
    },
    tourSteps() {
      const steps = [
        { target: "areas", title: tr("Kubi's sections, one tap away"), text: tr("Overview, Inventory, Menu & Recipes, Purchasing, Cost and Administration. Each opens its pages in the row underneath.") },
        { target: "pages", title: tr("The pages of the section"), text: tr("The page you are on is highlighted. Everything works in this tab; Connect is one click away on the left.") },
        { target: "store", title: tr("Which store you are working on"), text: tr("Counts, deliveries and waste belong to one store. Head office can also pick All stores to compare them.") },
        { target: "find", title: tr("Find any page by name"), text: trf("Press {0} K anywhere in Inventory and type a few letters.", [this.isMac ? "⌘" : "Ctrl"]) },
      ];
      if (this.assistantAllowed) {
        steps.push({ target: "ask", title: tr("Let Connect do the typing"), text: tr("Drop an invoice, a price list or a count sheet here, or just ask. It shows you what it read before anything is saved.") });
      }
      steps.push({ target: "help", title: tr("Stuck? Ask the page"), text: tr("\"How this page works\" explains the page in a few steps. You can take this tour again from here too.") });
      return steps;
    },
  },
  watch: {
    "$route.fullPath"() {
      this.rememberPage();
      this.helpMenu = false;
      this.userMenu = false;
      this.placeInk();
      if (this.$refs.main) this.$refs.main.scrollTop = 0;
    },
    areas() {
      this.placeInk();
    },
    // A section with a different number of pages changes the second row.
    "here.area"() {
      this.placeInk();
    },
  },
  mounted() {
    this._onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        this.finder = !this.finder;
      } else if (e.key === "Escape") {
        this.helpMenu = false;
        this.userMenu = false;
      }
    };
    this._onDoc = (e) => {
      if (this.$refs.helpWrap && !this.$refs.helpWrap.contains(e.target)) this.helpMenu = false;
      if (this.$refs.userWrap && !this.$refs.userWrap.contains(e.target)) this.userMenu = false;
    };
    this._onResize = () => this.placeInk();
    window.addEventListener("keydown", this._onKey);
    document.addEventListener("click", this._onDoc);
    window.addEventListener("resize", this._onResize);
    this.rememberPage();
    this.placeInk();
    // Fonts settle after the first paint; measure again so the ink sits under the right tab.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => this.placeInk());
    // The first visit: a short tour, once per person and browser.
    this._tourTimer = setTimeout(() => {
      let seen = true;
      try { seen = !!localStorage.getItem(this.tourKey); } catch (e) { seen = true; }
      if (!seen && this.areas.length) this.tour = true;
    }, 900);
  },
  beforeUnmount() {
    window.removeEventListener("keydown", this._onKey);
    document.removeEventListener("click", this._onDoc);
    window.removeEventListener("resize", this._onResize);
    clearTimeout(this._tourTimer);
  },
  methods: {
    areaTarget,
    pageTarget,
    /** A section's tab: the page last opened in it (if this person may still open it), else its first page. */
    areaLink(a) {
      const last = this.lastInArea[a.title];
      if (last && a.pages.some((p) => p.name === last.name)) return last.query ? { name: last.name, query: last.query } : { name: last.name };
      return areaTarget(a);
    },
    rememberPage() {
      const { area, page } = this.here;
      if (!area || !page) return;
      // An inner page (the PO editor) is remembered as the tab it belongs to.
      const own = this.$route.name === page.name;
      const query = own && this.$route.query && Object.keys(this.$route.query).length ? { ...this.$route.query } : undefined;
      this.lastInArea = { ...this.lastInArea, [area.title]: { name: page.name, query } };
      try { sessionStorage.setItem(LAST_IN_AREA, JSON.stringify(this.lastInArea)); } catch (e) { /* private mode: first page */ }
    },
    placeInk() {
      this.$nextTick(() => {
        this.areaInk = this.inkFor(this.$refs.areas, ".iw-area.is-on");
        this.pageInk = this.inkFor(this.$refs.pages, ".iw-page.is-on");
      });
    },
    inkFor(nav, selector) {
      const el = nav && nav.querySelector(selector);
      if (!el) return { opacity: 0 };
      return { opacity: 1, width: el.offsetWidth + "px", transform: "translateX(" + el.offsetLeft + "px)" };
    },
    switchStore(value) {
      if (value === ALL) {
        if (!this.stateStores.current.allStores) this.$store.dispatch("setAllStores");
        return;
      }
      if (value === this.stateStores.current.storeHash) return;
      this.$store.dispatch("setStoreDetails", value);
    },
    backToConnect() {
      this.userMenu = false;
      let last = "/";
      try { last = sessionStorage.getItem(LAST_CONNECT_PAGE) || "/"; } catch (e) { last = "/"; }
      this.$router.push(last);
    },
    openFinder() {
      this.helpMenu = false;
      this.finder = true;
    },
    openPageHelp() {
      this.helpMenu = false;
      if (this.help.available) this.help.open = true;
    },
    startTour() {
      this.helpMenu = false;
      this.tour = true;
    },
    endTour() {
      this.tour = false;
      try { localStorage.setItem(this.tourKey, new Date().toISOString()); } catch (e) { /* private mode: it shows again next time */ }
    },
    logout() {
      this.userMenu = false;
      this.$store.dispatch("logoutService");
      this.$router.push("/login");
    },
  },
};
</script>

<style>
@import "../../views/Inventory/kit/inv-workspace.css";
</style>

<style scoped>
/* The panel has no box-sizing reset; the workspace's own frame sizes by its border box. */
.iw-top, .iw-top *, .iw-sub, .iw-sub *, .iw-main, .iw-pop, .iw-pop * { box-sizing: border-box; }
.iw-top {
  position: fixed; top: 0; left: 0; right: 0; z-index: 25;
  height: var(--topbar-height);
  display: flex; align-items: center; gap: var(--s-3);
  padding: 0 var(--s-4) 0 var(--s-5);
  background: var(--bg-sidebar);
  border-bottom: 1px solid var(--sidebar-border);
}
/* A hairline of the brand colour under the header: the one branded touch. */
.iw-top::after {
  content: ""; position: absolute; left: 0; right: 0; bottom: -1px; height: 1px; pointer-events: none;
  background: linear-gradient(90deg, transparent 0%, var(--accent-primary) 22%, var(--accent-primary) 78%, transparent 100%);
  opacity: 0.45;
}

.iw-brand { display: flex; align-items: center; gap: var(--s-3); text-decoration: none; flex: 0 0 auto; }
.iw-logo {
  width: 32px; height: 32px; border-radius: var(--r-md);
  display: inline-flex; align-items: center; justify-content: center;
  background: var(--gradient-accent); color: #fff;
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.08) inset, 0 4px 14px rgba(249, 115, 22, 0.28);
}
.iw-logo svg { width: 20px; height: 20px; overflow: visible; }
.iw-logo path { transition: transform var(--iw-base) var(--iw-ease); }
/* The layers lift apart under the pointer: a small thing that says "stock". */
.iw-brand:hover .iw-logo-l1 { transform: translateY(-1.5px); }
.iw-brand:hover .iw-logo-l3 { transform: translateY(1.5px); }
.iw-brand-text { display: flex; flex-direction: column; line-height: 1.1; }
.iw-brand-name { font-size: var(--t-heading-sm); font-weight: var(--w-semibold); color: var(--text-primary); letter-spacing: -0.01em; }
.iw-brand-sub { font-size: var(--t-caption); color: var(--text-muted); }

.iw-back {
  display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 var(--s-3) 0 var(--s-2);
  border: 1px solid var(--border-primary); border-radius: 999px; background: transparent; cursor: pointer;
  color: var(--text-secondary); font-family: inherit; font-size: var(--t-body-sm); font-weight: var(--w-medium); flex: 0 0 auto;
  transition: background var(--iw-fast) var(--iw-ease), color var(--iw-fast) var(--iw-ease), border-color var(--iw-fast) var(--iw-ease);
}
.iw-back:hover { background: var(--bg-hover); color: var(--text-primary); border-color: var(--border-secondary); }
.iw-back .inv-icon { width: 15px; height: 15px; transition: transform var(--iw-fast) var(--iw-ease); }
.iw-back:hover .inv-icon { transform: translateX(-2px); }
.iw-rule { width: 1px; height: 24px; background: var(--border-primary); flex: 0 0 auto; }

.iw-areas { position: relative; display: flex; align-items: center; gap: 2px; min-width: 0; overflow-x: auto; scrollbar-width: none; flex: 1 1 auto; }
.iw-areas::-webkit-scrollbar { display: none; }
.iw-area {
  display: inline-flex; align-items: center; gap: var(--s-2); height: 36px; padding: 0 var(--s-3);
  border-radius: var(--r-md); white-space: nowrap; text-decoration: none;
  color: var(--sidebar-text, var(--text-secondary)); font-size: var(--t-body); font-weight: var(--w-medium);
  transition: background var(--iw-fast) var(--iw-ease), color var(--iw-fast) var(--iw-ease);
}
.iw-area:hover { background: var(--bg-hover); color: var(--text-primary); }
.iw-area.is-on { color: var(--text-primary); }
.iw-area.is-on .iw-area-icon { color: var(--accent-primary); }
.iw-area-icon { width: 17px; height: 17px; flex: 0 0 auto; transition: color var(--iw-fast) var(--iw-ease); }
.iw-ink {
  position: absolute; left: 0; bottom: -10px; height: 2px; border-radius: 2px; pointer-events: none;
  background: var(--gradient-accent);
  transition: transform var(--iw-base) var(--iw-ease), width var(--iw-base) var(--iw-ease), opacity var(--iw-fast) linear;
}
.iw-ink.is-sub { bottom: -8px; }

.iw-tools { display: flex; align-items: center; gap: var(--s-1); margin-left: auto; flex: 0 0 auto; }
.iw-tool {
  position: relative; width: 36px; height: 36px; display: inline-flex; align-items: center; justify-content: center;
  border: 0; border-radius: var(--r-md); background: transparent; color: var(--sidebar-text, var(--text-secondary)); cursor: pointer; flex: 0 0 auto;
  transition: background var(--iw-fast) var(--iw-ease), color var(--iw-fast) var(--iw-ease);
}
.iw-tool .inv-icon { width: 18px; height: 18px; }
.iw-tool:hover { background: var(--bg-hover); color: var(--text-primary); }
.iw-tool.is-on { background: var(--sidebar-active-bg, var(--accent-primary-bg)); color: var(--sidebar-active-text, var(--accent-primary)); }
.iw-ask { color: var(--accent-primary); }
.iw-menu-btn { display: none; }
.iw-badge {
  position: absolute; top: 4px; right: 4px; min-width: 16px; height: 16px; padding: 0 4px; border-radius: 999px;
  background: var(--accent-primary); color: #fff; font-size: 10px; line-height: 16px; font-weight: var(--w-bold); text-align: center;
}

.iw-store {
  display: flex; align-items: center; gap: var(--s-2); height: 36px; padding: 0 var(--s-2) 0 var(--s-3); margin-right: var(--s-1);
  border: 1px solid var(--border-primary); border-radius: var(--r-md); background: var(--bg-input); max-width: 15rem;
}
.iw-store-icon { width: 16px; height: 16px; color: var(--text-muted); flex: 0 0 auto; }
.iw-select { border: 0; background: transparent; color: var(--text-primary); font-family: inherit; font-size: var(--t-body-sm); font-weight: var(--w-medium); outline: none; cursor: pointer; max-width: 11rem; width: auto; height: auto; padding: 0; }
.iw-select option { background: var(--bg-primary); color: var(--text-primary); }

.iw-find {
  display: inline-flex; align-items: center; gap: var(--s-2); height: 36px; padding: 0 var(--s-2) 0 var(--s-3); margin-right: var(--s-1);
  border: 1px solid var(--border-primary); border-radius: var(--r-md); background: var(--bg-input); cursor: pointer;
  color: var(--text-muted); font-family: inherit; font-size: var(--t-body-sm); font-weight: var(--w-medium);
  transition: border-color var(--iw-fast) var(--iw-ease), color var(--iw-fast) var(--iw-ease);
}
.iw-find:hover { border-color: var(--border-secondary); color: var(--text-primary); }
.iw-find .inv-icon { width: 16px; height: 16px; }
.iw-find kbd {
  font-family: inherit; font-size: 11px; line-height: 1; font-weight: var(--w-semibold); padding: 3px 6px; border-radius: var(--r-sm);
  border: 1px solid var(--border-primary); background: var(--bg-primary); color: var(--text-muted);
}

.iw-user { border: 0; background: transparent; padding: 2px; border-radius: 999px; cursor: pointer; margin-left: var(--s-1); }
.iw-avatar {
  width: 32px; height: 32px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center;
  background: var(--gradient-accent); color: #fff; font-size: 12px; font-weight: var(--w-bold); letter-spacing: 0.02em;
}

.iw-pop-wrap { position: relative; }
.iw-pop {
  position: absolute; top: calc(100% + 8px); right: 0; z-index: 60; min-width: 17rem; padding: var(--s-1);
  background: var(--bg-modal, var(--bg-primary)); border: 1px solid var(--border-primary); border-radius: var(--r-lg);
  box-shadow: var(--shadow-overlay);
}
.iw-pop-user { min-width: 14rem; }
.iw-pop-head { display: flex; flex-direction: column; padding: var(--s-2) var(--s-3); margin-bottom: var(--s-1); border-bottom: 1px solid var(--border-light); }
.iw-pop-head strong { font-size: var(--t-body); color: var(--text-primary); }
.iw-pop-head small { font-size: var(--t-caption); color: var(--text-muted); }
.iw-pop-item {
  display: flex; align-items: flex-start; gap: var(--s-3); width: 100%; padding: var(--s-2) var(--s-3); text-align: left;
  border: 0; border-radius: var(--r-md); background: transparent; color: var(--text-primary); cursor: pointer; font-family: inherit;
}
.iw-pop-item:hover:not(:disabled) { background: var(--bg-hover); }
.iw-pop-item:disabled { opacity: 0.5; cursor: default; }
.iw-pop-item .inv-icon { width: 18px; height: 18px; margin-top: 1px; color: var(--accent-primary); flex: 0 0 auto; }
.iw-pop-item span { display: flex; flex-direction: column; gap: 2px; }
.iw-pop-item strong { font-size: var(--t-body); font-weight: var(--w-medium); }
.iw-pop-item small { font-size: var(--t-caption); color: var(--text-muted); }
.iw-pop-enter-active, .iw-pop-leave-active { transition: opacity var(--iw-fast) linear, transform var(--iw-fast) var(--iw-ease); }
.iw-pop-enter-from, .iw-pop-leave-to { opacity: 0; transform: translateY(-4px); }

/* The section's pages: a quiet second row under the header. */
.iw-sub {
  position: fixed; top: var(--topbar-height); left: 0; right: var(--drawer-width, 0px); z-index: 20;
  height: var(--iw-sub-height); display: flex; align-items: center; padding: 0 var(--s-5);
  background: var(--bg-sidebar);
  border-bottom: 1px solid var(--border-light);
}
.iw-pages { position: relative; display: flex; align-items: center; gap: 2px; overflow-x: auto; scrollbar-width: none; max-width: 100%; }
.iw-pages::-webkit-scrollbar { display: none; }
.iw-page {
  display: inline-flex; align-items: center; gap: 6px; height: 32px; padding: 0 var(--s-3); border-radius: var(--r-md);
  white-space: nowrap; text-decoration: none; color: var(--text-secondary); font-size: var(--t-body-sm); font-weight: var(--w-medium);
  transition: background var(--iw-fast) var(--iw-ease), color var(--iw-fast) var(--iw-ease);
}
.iw-page:hover { background: var(--bg-hover); color: var(--text-primary); }
.iw-page.is-on { color: var(--text-primary); background: var(--accent-primary-bg); }
.iw-page-icon { width: 15px; height: 15px; color: var(--text-muted); }
.iw-page.is-on .iw-page-icon { color: var(--accent-primary); }

.iw-main {
  margin-top: calc(var(--topbar-height) + var(--iw-sub-height));
  height: calc(100vh - var(--topbar-height) - var(--iw-sub-height));
  overflow-y: auto; overscroll-behavior: contain;
  padding: var(--s-6) calc(var(--drawer-width, 0px) + var(--s-6)) var(--s-9) var(--s-6);
}
.iw-main.no-sub { margin-top: var(--topbar-height); height: calc(100vh - var(--topbar-height)); }
/* Wide tables (Inventory Items has 12 columns) get room on big screens; the page still never stretches edge to edge. */
.iw-canvas { max-width: 1440px; margin: 0 auto; }

@media (max-width: 1280px) {
  .iw-brand-sub, .iw-find-text { display: none; }
}
@media (max-width: 1100px) {
  /* Icons only for the sections; the name is the tab's tooltip and the page row says where you are. */
  .iw-area-text { display: none; }
  .iw-area { padding: 0 10px; }
  .iw-find kbd { display: none; }
}
@media (max-width: 760px) {
  .iw-top { padding: 0 var(--s-2); gap: var(--s-2); }
  .iw-areas, .iw-rule, .iw-brand-text, .iw-find, .iw-back span { display: none; }
  .iw-back { padding: 0 var(--s-2); }
  .iw-menu-btn { display: inline-flex; }
  .iw-store { max-width: 10rem; }
  .iw-select { max-width: 7rem; }
  .iw-sub { padding: 0 var(--s-2); }
  .iw-main { padding: var(--s-4) var(--s-3) var(--s-8); }
}
</style>
